import { Cloud, Globe, Package, Smartphone, Layout, Users, Cpu, Database } from 'lucide-react';

export default function Services() {
  const services = [
    {
      icon: Cloud,
      title: 'Cloud Development & Migration',
      description: 'Transform your infrastructure with scalable, secure, and cost-effective cloud solutions.',
      features: [
        'AWS, Azure, Google Cloud expertise',
        'Cloud architecture design',
        'Legacy system migration',
        'DevOps & CI/CD implementation',
        'Cloud security & compliance'
      ]
    },
    {
      icon: Globe,
      title: 'Web Design & Development',
      description: 'Create powerful, responsive web applications that deliver exceptional user experiences.',
      features: [
        'Custom web application development',
        'Progressive Web Apps (PWA)',
        'E-commerce solutions',
        'CMS development & integration',
        'API development & integration'
      ]
    },
    {
      icon: Package,
      title: 'Enterprise ERP Solutions',
      description: 'Streamline business operations with comprehensive enterprise resource planning systems.',
      features: [
        'SAP implementation & customization',
        'Oracle ERP consulting',
        'Microsoft Dynamics integration',
        'Custom ERP development',
        'ERP migration & upgrade'
      ]
    },
    {
      icon: Cpu,
      title: 'Product Development',
      description: 'Build innovative digital products from ideation to launch with our full-stack expertise.',
      features: [
        'MVP development',
        'Product strategy & roadmap',
        'Full-cycle development',
        'Quality assurance & testing',
        'Maintenance & support'
      ]
    },
    {
      icon: Smartphone,
      title: 'Mobile App Development',
      description: 'Develop native and cross-platform mobile applications for iOS and Android.',
      features: [
        'Native iOS & Android apps',
        'React Native & Flutter',
        'Mobile UI/UX design',
        'App store optimization',
        'Backend integration'
      ]
    },
    {
      icon: Layout,
      title: 'UI/UX Design',
      description: 'Design intuitive, engaging interfaces that delight users and drive conversions.',
      features: [
        'User research & personas',
        'Wireframing & prototyping',
        'Visual design & branding',
        'Usability testing',
        'Design system creation'
      ]
    },
    {
      icon: Users,
      title: 'Staff Augmentation',
      description: 'Scale your team with certified professionals who integrate seamlessly with your workflow.',
      features: [
        'Dedicated development teams',
        'Flexible engagement models',
        'Vetted technical experts',
        'Quick onboarding process',
        'Full project management'
      ]
    },
    {
      icon: Database,
      title: 'Workflow Automation',
      description: 'Optimize operations with intelligent automation solutions powered by AI and ML.',
      features: [
        'Business process automation',
        'RPA implementation',
        'AI/ML integration',
        'Data analytics & insights',
        'Custom automation tools'
      ]
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-4">
            <span className="text-blue-700 font-semibold text-sm">OUR SERVICES</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive IT Services for{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Modern Enterprises
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From cloud infrastructure to mobile apps, we deliver end-to-end technology solutions that drive business growth and operational excellence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-transparent hover:border-blue-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <service.icon className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                {service.title}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-3">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button className="mt-6 text-blue-600 font-semibold flex items-center space-x-2 group-hover:space-x-3 transition-all">
                <span>Learn More</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
